import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  LayoutGrid, Users, Banknote, TrendingUp, Activity, ShieldCheck,
  Wallet, Clock, AlertTriangle, RefreshCw, Building2, FileDown,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const Kpi = ({ icon: Icon, label, value, sub, accent = 'pine', testId }) => {
  const a = {
    pine: 'bg-cn-primary/10 text-cn-primary',
    terracotta: 'bg-cn-terracotta-50 text-cn-terracotta-600',
    amber: 'bg-amber-50 text-amber-600',
    sage: 'bg-cn-sage-100 text-cn-primary',
  }[accent];
  return (
    <div className="cn-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <span className={`w-8 h-8 rounded-xl flex items-center justify-center ${a}`}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="mt-2 font-display font-bold text-2xl text-cn-text dark:text-white" data-testid={`${testId}-value`}>
        {value}
      </div>
      {sub && <div className="text-xs text-cn-text-muted mt-0.5">{sub}</div>}
    </div>
  );
};

export default function OperationsExecutive() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingPack, setDownloadingPack] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/operations/executive`, getAuthHeader());
      setData(data);
    } catch (e) {
      toast.error('Could not load the executive dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  const downloadPdf = useCallback(async () => {
    setDownloadingPdf(true);
    try {
      const headers = getAuthHeader().headers || {};
      const resp = await axios.get(`${API}/operations/exec-scorecard.pdf`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom_exec_scorecard_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Executive scorecard PDF downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the PDF.');
    } finally {
      setDownloadingPdf(false);
    }
  }, [getAuthHeader]);

  const downloadPack = useCallback(async () => {
    setDownloadingPack(true);
    try {
      const headers = getAuthHeader().headers || {};
      const resp = await axios.get(`${API}/admin/exec-pdf-pack.pdf`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `caraloom_exec_pack_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Executive Pack PDF downloaded — board-ready.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not generate the Executive Pack.');
    } finally {
      setDownloadingPack(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const navItems = [
    { icon: LayoutGrid, label: 'Executive', active: true },
    { icon: Activity, label: 'Operations · Census', href: '#' },
    { icon: Users, label: 'Workforce', href: '/agency/find-clinicians' },
    { icon: Banknote, label: 'Revenue Cycle', href: '/clinician/billing' },
    { icon: ShieldCheck, label: 'Compliance', href: '/admin/safety-shield' },
    { icon: Building2, label: 'Agency dashboard', href: '/agency/dashboard' },
  ];

  const arBuckets = data?.ar?.buckets || { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  const arMax = Math.max(1, ...Object.values(arBuckets));

  return (
    <DashboardShell
      navItems={navItems}
      title="Executive Dashboard"
      subtitle="Census · Productivity · A/R · Revenue forecast — the morning ritual."
      testId="ops-executive-page"
      rightSlot={
        <div className="flex gap-2">
          <Button
            onClick={downloadPack}
            disabled={downloadingPack || loading}
            className="cn-btn cn-btn-primary !py-2.5"
            data-testid="ops-executive-pack"
            title="One-click board-ready PDF bundling every CaraLoom analytic"
          >
            <FileDown className={`w-4 h-4 ${downloadingPack ? 'animate-pulse' : ''}`} />
            {downloadingPack ? 'Building Pack…' : 'Executive Pack PDF'}
          </Button>
          <Button
            onClick={downloadPdf}
            disabled={downloadingPdf || loading}
            className="cn-btn cn-btn-ghost !py-2.5"
            data-testid="ops-executive-pdf"
          >
            <FileDown className={`w-4 h-4 ${downloadingPdf ? 'animate-pulse' : ''}`} />
            {downloadingPdf ? 'Building PDF…' : 'Export PDF'}
          </Button>
          <Button
            onClick={load}
            disabled={refreshing}
            className="cn-btn cn-btn-ghost !py-2.5"
            data-testid="ops-executive-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="grid sm:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="cn-card h-28 animate-pulse bg-cn-bg/60" />)}
        </div>
      ) : (
        <>
          {/* 4 hero KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ops-kpi-row">
            <Kpi
              icon={Activity}
              label="Active census"
              value={data?.census?.active ?? 0}
              sub={`${data?.census?.new_admissions_30d ?? 0} new admissions / 30d`}
              accent="pine"
              testId="ops-kpi-census"
            />
            <Kpi
              icon={Users}
              label="Visits per clinician / day"
              value={data?.productivity?.visits_per_clinician_per_day ?? 0}
              sub={`${data?.productivity?.visits_30d ?? 0} visits · ${data?.productivity?.unique_clinicians_30d ?? 0} clinicians (30d)`}
              accent="sage"
              testId="ops-kpi-productivity"
            />
            <Kpi
              icon={Wallet}
              label="Outstanding A/R"
              value={money(data?.ar?.total_outstanding)}
              sub={`${data?.ar?.open_claim_count ?? 0} open claims`}
              accent="terracotta"
              testId="ops-kpi-ar"
            />
            <Kpi
              icon={TrendingUp}
              label="Forecast · next 30d"
              value={money(data?.revenue_forecast?.next_30)}
              sub={`Banked last 30d: ${money(data?.revenue_forecast?.collected_last_30)}`}
              accent="amber"
              testId="ops-kpi-forecast"
            />
          </div>

          {/* Census trend chart */}
          <div className="cn-card p-6 mt-6" data-testid="ops-census-trend">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-cn-primary" />
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Census · 30-day new admissions</h3>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data?.census?.trend || []}>
                <defs>
                  <linearGradient id="censusFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0E3D3A" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#0E3D3A" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="admissions" stroke="#0E3D3A" fillOpacity={1} fill="url(#censusFill)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Productivity chart */}
          <div className="cn-card p-6 mt-6" data-testid="ops-visits-trend">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-4 h-4 text-cn-primary" />
              <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Productivity · daily completed visits</h3>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data?.productivity?.trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="visits" stroke="#14B8A6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* A/R Aging + Revenue Forecast side by side */}
          <div className="grid lg:grid-cols-2 gap-4 mt-6">
            <div className="cn-card p-6" data-testid="ops-ar-aging">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-cn-primary" />
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">A/R aging</h3>
              </div>
              <div className="space-y-3">
                {Object.entries(arBuckets).map(([bucket, amt]) => (
                  <div key={bucket} data-testid={`ops-ar-bucket-${bucket}`}>
                    <div className="flex items-end justify-between mb-1">
                      <span className="text-xs font-semibold text-cn-text-muted">{bucket} days</span>
                      <span className="font-display font-bold text-cn-text dark:text-white">{money(amt)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-cn-bone-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${bucket === '90+' ? 'bg-red-400' : bucket === '61-90' ? 'bg-amber-400' : 'bg-cn-primary'}`}
                        style={{ width: `${(amt / arMax) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="cn-card p-6" data-testid="ops-revenue-forecast">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-cn-primary" />
                <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white">Revenue forecast</h3>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div data-testid="ops-forecast-30">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Next 30d</div>
                  <div className="font-display font-bold text-xl text-cn-primary">{money(data?.revenue_forecast?.next_30)}</div>
                </div>
                <div data-testid="ops-forecast-60">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Next 60d</div>
                  <div className="font-display font-bold text-xl text-cn-primary">{money(data?.revenue_forecast?.next_60)}</div>
                </div>
                <div data-testid="ops-forecast-90">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Next 90d</div>
                  <div className="font-display font-bold text-xl text-cn-primary">{money(data?.revenue_forecast?.next_90)}</div>
                </div>
              </div>
              <p className="text-[11px] text-cn-text-muted mt-4">
                Probability-weighted from open claim status × payer history.
                Priors recalibrate automatically as fresh claims data lands.
              </p>
            </div>
          </div>

          <p className="text-[11px] text-cn-text-muted mt-6" data-testid="ops-generated-at">
            Generated at {data?.generated_at ? new Date(data.generated_at).toLocaleString() : '—'} · Scope: {data?.scope}
          </p>
        </>
      )}
    </DashboardShell>
  );
}
